// The CAD check for one job: the export that was imported, the job's own side read from the
// resolved job, and the differences between them, worked out fresh every time.

import { useMemo } from 'react';
import {
  diffComparison, jobComparisonRows, unresolvedDifferences,
  type CompareRow, type Difference, type Library, type Resolution, type SourceMapping,
} from '@zerogap/engine';
import { useRows, type Row } from './api';
import { useSession } from './session';

export interface CadCheckSettings {
  sheet_categories?: string[];
  size_tolerance_in?: number | string;
  resolutions?: { key: string; label: string }[];
}

export interface CadCheck {
  loading: boolean;
  sourceTypes: SourceMapping[];
  settings: CadCheckSettings;
  /** Every export imported onto the job, newest first. The newest is the one compared. */
  sources: Row[];
  source: Row | null;
  diffs: Difference[];
  resolutions: Row[];
  /** Differences with no resolution yet. The CAD check step cannot close while there is one. */
  open: Difference[];
  /** Why the step cannot close, in words, or null when it can. */
  blocker: string | null;
}

export function useCadCheck(jobId: string, res: Resolution | null, lib: Library | null): CadCheck {
  const s = useSession();
  const sourceTypes = s.setting<SourceMapping[]>('comparison_source_types', []);
  const settings = s.setting<CadCheckSettings>('cad_check', {});
  const sources = useRows('comparison_source', { eq: { job_id: jobId }, order: ['-created_at'], enabled: !!jobId });
  const source = sources.rows[0] ?? null;
  const resolutions = useRows('comparison_resolution', { eq: { comparison_source_id: source?.id }, enabled: !!source });

  return useMemo(() => {
    const diffs = source && res && lib
      ? diffComparison((source.rows ?? []) as CompareRow[], jobComparisonRows(res, lib, settings.sheet_categories ?? []), { toleranceInches: settings.size_tolerance_in ?? 0 })
      : [];
    const open = unresolvedDifferences(diffs, resolutions.rows as { diff_key: string; resolution: string }[]);
    const blocker = !source
      ? 'No CAD export has been imported for this job yet.'
      : open.length > 0
        ? `${open.length} ${open.length === 1 ? 'difference between the CAD export and the items is' : 'differences between the CAD export and the items are'} not resolved.`
        : null;
    return { loading: sources.loading || resolutions.loading, sourceTypes, settings, sources: sources.rows, source, diffs, resolutions: resolutions.rows, open, blocker };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources.rows, sources.loading, resolutions.rows, resolutions.loading, res, lib, sourceTypes, settings]);
}
